import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import { useTranslation } from 'next-i18next'
import React, { SyntheticEvent, useEffect, useState } from 'react'
import useLogger from 'src/context/LoggerProvider/useLogger'
import useUserLocation from 'src/hooks/useUserLocation'
import {
  CDMX_OPTION,
  MTY_OPTION,
  NEAR_USER,
  NEAR_USER_OPTION
} from '../../constants'
import { useDebounce } from '../../hooks/useDebounce'
import {
  IGoogleAutocompleteSuggestion,
  IGooglePlaceDetails,
  IQuery,
  IQueryGoogleAutocompleteSuggestionsArgs
} from '../../types/graphql'

const AUTOCOMPLETE_SUGGESTIONS = gql`
  query Autocomplete($searchValue: String!) {
    googleAutocompleteSuggestions(searchValue: $searchValue) {
      placeId
      description
      friendlyDisplay
    }
  }
`

type IProps = {
  dontClearSearch?: boolean
  handleOnSelect: (
    place: IGoogleAutocompleteSuggestion,
    search: string,
    coords?: IGooglePlaceDetails | undefined
  ) => void
  hideCloseByOption?: boolean
  debounceMs?: number
}

const useAutocomplete = ({
  dontClearSearch,
  handleOnSelect,
  hideCloseByOption,
  debounceMs = 700
}: IProps) => {
  const { t } = useTranslation('common')
  const logger = useLogger()
  const { getUserLocation } = useUserLocation()

  const [searchValue, setSearch] = useState<string>('')
  const debouncedValue = useDebounce(searchValue, debounceMs)
  const [checked, setChecked] = useState(false)
  const MIN_AUTOCOMPLETE_CHARS = 3
  // True while the user is typing but the debounce hasn't fired yet — lets
  // consumers show a spinner immediately instead of dead air before the query.
  const pending =
    searchValue !== debouncedValue &&
    searchValue.trim().length >= MIN_AUTOCOMPLETE_CHARS

  const [suggestions, setSuggestions] = useState<
    IGoogleAutocompleteSuggestion[]
  >([])

  /** Autocomplete Logic */
  const [
    loadSuggestions,
    { data: suggestionsData, error: suggestionsError, loading }
  ] = useLazyQuery<
    Pick<IQuery, 'googleAutocompleteSuggestions'>,
    IQueryGoogleAutocompleteSuggestionsArgs
  >(AUTOCOMPLETE_SUGGESTIONS)

  useEffect(() => {
    if (suggestionsData?.googleAutocompleteSuggestions) {
      setSuggestions(
        suggestionsData.googleAutocompleteSuggestions as IGoogleAutocompleteSuggestion[]
      )
    }
  }, [suggestionsData])

  useEffect(() => {
    if (suggestionsError) {
      logger.error(
        'Error retrieving suggestions',
        { searchValue: debouncedValue },
        suggestionsError
      )
      setSuggestions([])
      setSearch('')
    }
  }, [suggestionsError, debouncedValue, logger])

  useEffect(() => {
    const trimmed = debouncedValue?.trim() ?? ''
    if (trimmed.length >= MIN_AUTOCOMPLETE_CHARS) {
      loadSuggestions({ variables: { searchValue: trimmed } })
    } else if (trimmed.length > 0) {
      setSuggestions([])
    }
  }, [debouncedValue, loadSuggestions])

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { value }
    } = event
    setSearch(value)
    if (value !== '') {
      setChecked(true)
    } else {
      setChecked(false)
    }
  }

  const clearSearch = () => {
    if (dontClearSearch) return
    setSearch('')
    setChecked(false)
  }

  const handleOnChangeAutocomplete = async (
    event: SyntheticEvent<Element, Event>,
    value: string | IGoogleAutocompleteSuggestion | null
  ) => {
    if (value && typeof value !== 'string') {
      event.preventDefault()
      handleOnSelect(
        value,
        searchValue,
        value.placeId === NEAR_USER ? await getUserLocation() : undefined
      )
      clearSearch()
    }
  }

  const handleOnClick = () => {
    const fallback = hideCloseByOption
      ? undefined
      : [
          {
            ...NEAR_USER_OPTION,
            description: t('SEARCH_MANAGER.NEAR_TO_ME'),
            friendlyDisplay: t('SEARCH_MANAGER.NEAR_TO_ME')
          }
        ]
    handleOnSelect(suggestions[0] ?? fallback, searchValue)
    clearSearch()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && suggestions.length > 0) {
      handleOnSelect(suggestions[0], searchValue)
      clearSearch()
    }
  }

  const defaults = hideCloseByOption ? [] : [CDMX_OPTION, MTY_OPTION]
  const nearMeOptions = hideCloseByOption
    ? []
    : [
        {
          ...NEAR_USER_OPTION,
          description: t('SEARCH_MANAGER.NEAR_TO_ME'),
          friendlyDisplay: t('SEARCH_MANAGER.NEAR_TO_ME')
        }
      ]
  const options =
    typeof window !== 'undefined' &&
    window.navigator.geolocation &&
    searchValue !== ''
      ? [...suggestions, ...nearMeOptions]
      : [...defaults, ...nearMeOptions]

  return {
    searchValue,
    checked,
    options,
    loading,
    pending,
    handleOnChangeAutocomplete,
    handleOnChange,
    handleKeyDown,
    handleOnClick,
    clearSearch
  }
}

export default useAutocomplete
